<script setup lang="ts">
import { BarChart } from '@/components/ui/chart-bar';
import { dashboardApiClient } from '@api-client/dashboard';
import type { ResType } from '@api-client/typeUtils';
import type { DateRangeValueType } from '@lib/zod/dateTimePropEditor';
import { toast } from '@/ui-plus/sonner';
import { computed, onMounted, ref } from 'vue';
import { Loader2 } from 'lucide-vue-next';
import { currentWebsiteId } from '@partner-spa-vue/stores/MyWebsitesStore';
import { fillDailyGaps, rollupWeekly } from '@partner-spa-vue/components/partnerReportChartUtils';

const props = defineProps<{
    partnerType: 'marketing' | 'support';
    dateRange: DateRangeValueType;
    granularity: 'daily' | 'weekly';
    currency: string;
    storeId?: string;
    deliveryteamId?: string;
}>();

type DailyRow = ResType<typeof dashboardApiClient.website.partner_marketing.store_reports[':websiteId']['$post']>['data']['reports'][number]['daily'][number];

const daily = ref<DailyRow[]>([]);
const isLoading = ref(true);

const isMarketing = computed(() => props.partnerType === 'marketing');

const dailyChartData = computed(() =>
    fillDailyGaps(
        daily.value,
        props.dateRange.start ?? 0,
        props.dateRange.end ?? Date.now(),
    ),
);

const chartData = computed(() =>
    props.granularity === 'weekly'
        ? rollupWeekly(dailyChartData.value)
        : dailyChartData.value,
);

const periodLabel = computed(() =>
    props.granularity === 'weekly' ? 'Weekly' : 'Daily',
);

onMounted(async () => {
    isLoading.value = true;
    const websiteId = currentWebsiteId.value || '';
    const json = {
        start: props.dateRange.start,
        end: props.dateRange.end,
        ...(props.storeId
            ? { storeId: props.storeId }
            : { deliveryteamId: props.deliveryteamId }),
    };

    const res = props.partnerType === 'marketing'
        ? await (await dashboardApiClient.website.partner_marketing.store_reports[':websiteId'].$post({
            param: { websiteId },
            json,
        })).json()
        : await (await dashboardApiClient.website.partner_support.store_reports[':websiteId'].$post({
            param: { websiteId },
            json,
        })).json();

    isLoading.value = false;

    if (res.error) {
        toast.error('Failed to load daily chart');
        console.error(res.error);
        return;
    }
    if (res.data) {
        const match = res.data.reports.find(r => r.currency === props.currency);
        daily.value = match?.daily ?? [];
    }
});
</script>

<template>
    <div class="py-2 space-y-4">
        <Loader2 v-if="isLoading"
                 class="animate-spin h-8 w-8" />
        <template v-else-if="chartData.length > 0">
            <div class="p-4 border rounded-md">
                <p class="text-sm font-medium mb-2">
                    {{ periodLabel }} orders
                </p>
                <BarChart :data="chartData.map(row => ({ date: row.date, count: row.count }))"
                          index="date"
                          :categories="['count']"
                          :rounded-corners="1"
                          class="h-[140px]" />
            </div>
            <div class="p-4 border rounded-md">
                <p class="text-sm font-medium mb-2">
                    {{ periodLabel }} GMV
                </p>
                <BarChart :data="chartData.map(row => ({ date: row.date, total: row.total }))"
                          index="date"
                          :categories="['total']"
                          :rounded-corners="1"
                          class="h-[140px]" />
            </div>
            <div class="p-4 border rounded-md">
                <p class="text-sm font-medium mb-2">
                    {{ periodLabel }} earnings
                </p>
                <BarChart :data="chartData.map(row => ({ date: row.date, earnings: row.earnings }))"
                          index="date"
                          :categories="['earnings']"
                          :rounded-corners="1"
                          class="h-[140px]" />
            </div>
            <div v-if="isMarketing"
                 class="p-4 border rounded-md">
                <p class="text-sm font-medium mb-2">
                    {{ periodLabel }} support fees
                </p>
                <BarChart :data="chartData.map(row => ({ date: row.date, supportFees: row.supportFees }))"
                          index="date"
                          :categories="['supportFees']"
                          :rounded-corners="1"
                          class="h-[140px]" />
            </div>
            <div class="p-4 border rounded-md">
                <p class="text-sm font-medium mb-2">
                    {{ periodLabel }} SMS/Hosting fees
                </p>
                <BarChart :data="chartData.map(row => ({ date: row.date, platformFees: row.platformFees }))"
                          index="date"
                          :categories="['platformFees']"
                          :rounded-corners="1"
                          class="h-[140px]" />
            </div>
        </template>
        <p v-else
           class="text-sm text-muted-foreground">
            No orders in this range
        </p>
    </div>
</template>
